import type { SupabaseClient, User } from "@supabase/supabase-js";
import { lockAccessFlow, type AccessFlow } from "../access-flow";

/** Browser hints never grant a role: identity and immutable flow come from the server. */
export async function authorizeRegisteredAccount(client: SupabaseClient, requested?: AccessFlow, options?: { adminMfaBootstrap?: boolean }): Promise<{ user: User; flow: AccessFlow }> {
  const { data, error } = await client.auth.getUser();
  if (error || !data.user || !data.user.email || !data.user.email_confirmed_at) {
    throw new Error("Confirme seu cadastro e seu e-mail antes de entrar.");
  }
  const binding = await client.rpc("bind_account_flow", { _flow: requested ?? null });
  if (binding.error || !["user", "venue", "admin"].includes(binding.data)) {
    throw new Error("Esta conta não pode acessar este fluxo. Entre pelo acesso do seu cadastro.");
  }
  const flow = binding.data as AccessFlow;
  if (requested && flow !== requested) throw new Error("Esta conta não pode acessar este fluxo.");
  if (flow === "venue") {
    const owner = await client.rpc("has_role", { _user_id: data.user.id, _role: "venue_owner" });
    if (owner.error || owner.data !== true) throw new Error("Finalize a aprovação do estabelecimento antes de acessar o painel.");
  }
  if (flow === "admin") {
    const role = options?.adminMfaBootstrap
      ? await client.rpc("admin_bootstrap_identity")
      : await client.rpc("has_role", { _user_id: data.user.id, _role: "admin" });
    const assurance = await client.auth.mfa.getAuthenticatorAssuranceLevel();
    if (role.error || role.data !== true || assurance.error || !assurance.data?.currentLevel || (!options?.adminMfaBootstrap && assurance.data.currentLevel !== "aal2")) {
      throw new Error("Confirme o acesso administrativo com o segundo fator.");
    }
  }
  return { user: data.user, flow };
}

/**
 * A fresh login must claim the account session before any other database call:
 * the server rejects every request from a session that has not been claimed yet,
 * so validating first would sign the person out right after a correct password.
 */
export async function claimFreshLoginSession(client: SupabaseClient): Promise<void> {
  const claim = await client.rpc("claim_account_session");
  if (claim.error || claim.data !== true) throw new Error("Não foi possível confirmar seu acesso. Tente entrar novamente.");
}

export async function signInRegisteredAccount(client: SupabaseClient, email: string, password: string, flow: AccessFlow, mfaCode?: string) {
  const current = await client.auth.getSession();
  if (current.error || current.data.session) throw new Error("Saia da conta atual antes de entrar em outra conta.");
  const result = await client.auth.signInWithPassword({ email: email.trim().toLowerCase(), password });
  if (result.error) throw new Error("E-mail ou senha incorretos, ou cadastro ainda não confirmado.");
  try {
    if (!result.data.session || !result.data.user) throw new Error("Não foi possível confirmar seu login.");
    await claimFreshLoginSession(client);
    if (flow === "admin" && mfaCode) {
      const factors = await client.auth.mfa.listFactors();
      const factor = factors.data?.totp.find(item => item.status === "verified");
      if (factors.error) throw new Error("Segundo fator administrativo não disponível.");
      if (factor) {
        const challenge = await client.auth.mfa.challengeAndVerify({ factorId: factor.id, code: mfaCode });
        if (challenge.error) throw new Error("Código do autenticador inválido.");
      }
    }
    // AAL1 is only a bootstrap identity; the administrative route mounts the
    // mandatory enrollment/challenge gate, never administrative children.
    const account = await authorizeRegisteredAccount(client, flow, { adminMfaBootstrap: flow === "admin" });
    // Presentation hint only: replaces a stale flow from an earlier account so
    // client guards do not treat this server-confirmed login as cross-flow.
    lockAccessFlow(account.flow);
    return account;
  } catch (error) {
    await client.auth.signOut({ scope: "local" });
    throw error;
  }
}
/**
 * Confirma o cadastro com o código enviado por e-mail, sem sair do app. Passa
 * pelas mesmas checagens do login (fluxo da conta no servidor) e encerra a
 * sessão local se algo falhar.
 */
export async function confirmSignupCode(client: SupabaseClient, email: string, code: string, flow: AccessFlow) {
  const token = code.replace(/\D/g, "");
  if (token.length < 6) throw new Error("Digite o código de 6 dígitos enviado para o seu e-mail.");
  const current = await client.auth.getSession();
  if (current.error || current.data.session) throw new Error("Saia da conta atual antes de confirmar outro cadastro.");
  const result = await client.auth.verifyOtp({ email: email.trim().toLowerCase(), token, type: "signup" });
  if (result.error || !result.data.session) throw new Error("Código inválido ou expirado. Confira o e-mail ou peça um novo código.");
  try {
    await claimFreshLoginSession(client);
    const account = await authorizeRegisteredAccount(client, flow);
    lockAccessFlow(account.flow);
    return account;
  } catch (error) {
    await client.auth.signOut({ scope: "local" });
    throw error;
  }
}

/** Reenvia o e-mail de confirmação do cadastro (com código e botão). */
export async function resendSignupCode(client: SupabaseClient, email: string, redirectTo?: string) {
  const { error } = await client.auth.resend({ type: "signup", email: email.trim().toLowerCase(), options: redirectTo ? { emailRedirectTo: redirectTo } : undefined });
  if (error) throw new Error("Não foi possível reenviar agora. Aguarde um minuto e tente de novo.");
}
